import {
  DetailedHTMLProps,
  HTMLAttributes,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  enterpriseDetailsDtoSchema,
  initialEnterpriseDetailsDto,
} from './form-config';

import ChevronDownAdornment from '@atoms/ChevronDownAdornment';
import { EnterpriseDetailsFormDto } from '@enterprise/models/enterprise-form';
import EnterpriseFormFooter from '../EnterpriseFormFooter';
import IOption from '@models/option';
import ImageUpload from '@atoms/ImageUpload';
import { JAutocomplete } from '@components/molucules/JAutocomplete';
import JTextFieldBase from '@atoms/JTextFieldBase';
import JTextFieldForm from '@atoms/JTextFieldForm';
import Jpaper from '@atoms/JPaper';
import LogoImageUploadViewer from '@atoms/LogoImageUploadViewer';
import { Stack } from '@mui/material';
import { UploadLogoPlaceholder } from '../../../../components/atoms/UploadLogoPlaceholder';
import useCreateEnterprise from '@enterprise/hooks/useCreateEnterprise';
import { useDebouncedCallback } from 'use-debounce';
import { useGetRomeDomainsPaged } from '@/queries/useGetRomeDomain';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';

interface UploadLogoPlaceholderWrapperProps
  extends DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> {}
const uploadLogoPlaceholderWrapper = (
  props: UploadLogoPlaceholderWrapperProps,
) => <UploadLogoPlaceholder {...props} required />;

interface EntrepriseFormDetailsProps {
  includeFooter: boolean;
}
const EnterpriseFormDetails = ({
  includeFooter,
}: EntrepriseFormDetailsProps) => {
  const [sectorInputValue, setSectorInputValue] = useState<string>('');
  const [isLogoValid, setIsLogoValid] = useState<boolean>();

  const {
    setStepIndex,
    payload,
    setPayload,
    setSectors,
    sectors,
    setIsDetailsFromValid,
    setIsDirty,
  } = useCreateEnterprise();
  const { data: romeDomains } = useGetRomeDomainsPaged(sectorInputValue);

  const { t } = useTranslation();

  const methods = useForm<EnterpriseDetailsFormDto>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: useMemo(
      () => initialEnterpriseDetailsDto,
      [initialEnterpriseDetailsDto],
    ),
    resolver: yupResolver(enterpriseDetailsDtoSchema(t)),
  });
  const { handleSubmit, setValue, formState, trigger, getValues, watch } =
    methods;

  const { isValid, isDirty } = formState;

  const onSubmit = handleSubmit((data) => {
    setStepIndex((prev) => (prev ? prev + 1 : 1));
  });

  const updateSectors = (options: IOption[]): void => {
    setSectors(options);
    setValue(
      'activityAreaIds',
      options.map((item) => item.value),
      { shouldDirty: true },
    );
    trigger('activityAreaIds');
  };

  useEffect(() => {
    if (!sectors || sectors?.length === 0) return;
    setValue(
      'activityAreaIds',
      sectors.map((item) => item.value),
    );
    trigger('activityAreaIds');
  }, []);

  const setLogo = (value: File): void => {
    setValue('logo', value, { shouldDirty: true });
    trigger('logo');
  };

  const debouncedSector = useDebouncedCallback((value) => {
    setSectorInputValue(value);
  }, 1000);

  useEffect(() => {
    setIsDetailsFromValid(isValid);
  }, [isValid]);

  useEffect(() => {
    if (isDirty) return;
    // Check if payload is available and update default values accordingly
    if (payload?.enterpriseDto) {
      setValue('name', payload.enterpriseDto.name);
      setValue('logo', payload.logo);
      setValue('siren', payload.enterpriseDto.siren);
      setValue('email', payload.enterpriseDto.email);
      setValue('description', payload.enterpriseDto.description);
      setValue('activityAreaIds', payload.enterpriseDto.activityAreaIds);
      trigger();
    }
  }, [payload, setValue]);

  useEffect(() => {
    return () => {
      const vals = getValues();
      const { logo, ...details } = vals;

      setPayload((prev) => {
        return {
          logo,
          addressDto: prev?.addressDto,
          enterpriseDto: {
            ...details,
          },
          video: prev?.video,
          thumbnail: prev?.thumbnail,
          logoUrl: prev?.logoUrl,
          thumbnailUrl: prev?.thumbnailUrl,
          videoUrl: prev?.videoUrl,
          id: prev?.id,
        };
      });
    };
  }, [getValues, setPayload]);

  useEffect(() => {
    setIsLogoValid(!!(getValues('logo') ?? payload?.logoUrl));
  }, [getValues('logo'), payload?.logoUrl]);

  useEffect(() => {
    setIsDirty(isDirty && isValid);
  }, [isDirty, isValid]);

  useEffect(() => {
    if (!isDirty) return;
    const { logo, ...details } = getValues();
    setPayload((prev) => {
      return {
        ...prev,
        enterpriseDto: {
          ...details,
        },
        logo,
      };
    });
  }, [
    getValues,
    setPayload,
    setIsDirty,
    isDirty,
    watch('description'),
    watch('name'),
    watch('siren'),
    watch('email'),
    watch('activityAreaIds'),
  ]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit} autoComplete="off" noValidate>
        <Stack gap={2}>
          <Jpaper gap={1} alignItems="stretch">
            <Stack alignItems={'center'} my={2}>
              <ImageUpload
                data-qa="logo-upload"
                ComponentUpload={(props) => uploadLogoPlaceholderWrapper(props)}
                ComponentView={LogoImageUploadViewer}
                value={payload?.logo}
                urlValue={payload?.logoUrl}
                onImageUpload={(file) => setLogo(file)}
              />
            </Stack>
            <JTextFieldForm
              required
              name="name"
              data-qa="name"
              fullWidth
              noBorder
              label={t('NAME_ENTERPRISE')}
            />
            <JTextFieldForm
              required
              data-qa="siren"
              name="siren"
              fullWidth
              noBorder
              label={t('SIREN')}
            />
            <JTextFieldForm
              name="email"
              data-qa="email"
              fullWidth
              noBorder
              label={t('EMAIL')}
            />
            <JTextFieldForm
              data-qa="description"
              required
              multiline
              rows={9}
              fullWidth
              name="description"
              noBorder
              label={t('DESCRIPTION')}
              onChange={(e) => {
                setValue('description', e.target.value, { shouldDirty: true });
                trigger('description');
              }}
            />

            <JAutocomplete
              data-qa="sectors"
              value={sectors ?? []}
              required
              multiple
              label={t('SECTOR')}
              fullWidth
              onChange={(ev, newValue) =>
                updateSectors(newValue as { value: string; label: string }[])
              }
              onTextChange={(value) => debouncedSector(value)}
              options={
                romeDomains?.data.data.map((item) => ({
                  value: item.id,
                  label: item.name,
                })) ?? []
              }
              renderInput={(params) => (
                <JTextFieldBase
                  data-qa="sectors-input"
                  required
                  label={t('SECTOR')}
                  onChange={(ev) => debouncedSector(ev.target.value)}
                  {...params}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: <ChevronDownAdornment />,
                    onKeyDown: (e) => {
                      if (e.key === 'Enter') {
                        e.stopPropagation();
                      }
                    },
                  }}
                />
              )}
            />
          </Jpaper>
          {includeFooter && (
            <EnterpriseFormFooter
              isValid={isValid && !!isLogoValid}
              values={getValues()}
            />
          )}
        </Stack>
      </form>
    </FormProvider>
  );
};

export default EnterpriseFormDetails;
